import { useFrame, useThree } from "@react-three/fiber";
import { useEffect, useRef } from "react";
import * as THREE from "three";

export default function CameraShakeRig({
  shakeTrigger = 0,
  intensity = 0.02,
  duration = 0.22,
  controlsRef,
  onShakeStart,
  onShakeEnd,
}) {
  const { camera } = useThree();

  const basePosition = useRef(camera.position.clone());
  const baseTarget = useRef(new THREE.Vector3(0, 0, 0));
  const shakeStartTime = useRef(null);
  const lastTrigger = useRef(shakeTrigger);
  const activeOffset = useRef(new THREE.Vector3());

  useEffect(() => {
    basePosition.current.copy(camera.position);

    if (controlsRef?.current) {
      baseTarget.current.copy(controlsRef.current.target);
    }
  }, [camera, controlsRef]);

  useFrame((state) => {
    const controls = controlsRef?.current;

    if (shakeTrigger !== lastTrigger.current) {
      lastTrigger.current = shakeTrigger;
      shakeStartTime.current = state.clock.elapsedTime;

      basePosition.current.copy(camera.position);

      if (controls) {
        baseTarget.current.copy(controls.target);
      }

      onShakeStart?.();
    }

    if (shakeStartTime.current === null) return;

    const elapsed = state.clock.elapsedTime - shakeStartTime.current;

    if (elapsed >= duration) {
      camera.position.copy(basePosition.current);

      if (controls) {
        controls.target.copy(baseTarget.current);
        controls.update();
      }

      activeOffset.current.set(0, 0, 0);
      shakeStartTime.current = null;
      onShakeEnd?.();
      return;
    }

    const t = elapsed / duration;
    const decay = Math.pow(1 - t, 2);

    // cinematic impulse instead of random buzz
    const impulse = Math.sin(t * Math.PI * 4.5) * decay;

    const x = impulse * intensity;
    const y = impulse * intensity * 0.45;
    const z = impulse * intensity * 0.18;

    activeOffset.current.set(x, y, z);

    camera.position.copy(basePosition.current).add(activeOffset.current);

    if (controls) {
      controls.target.copy(baseTarget.current).add(activeOffset.current);
      controls.update();
    }
  });

  return null;
}